import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/app-config.service';
import { DecodedDataTable } from '@app/features/decoded-data/models/decoded-data-table.model';
import { DecodedDataValueDto } from '@app/features/decoded-data/models/decoded-data-value-dto.model';
import { DecodedDataValue } from '@app/features/decoded-data/models/decoded-data-value.model';
import { Division } from '@app/features/division/models/division.interface';
import { DivisionService } from '@app/features/division/services/division.service';
import { Page } from '@app/shared/models/page.interface';
import { SearchParams } from '@app/shared/models/search-params.model';
import { EmptyTranslation } from '@app/shared/models/translation/translation.interface';
import { DataTablePageState } from '@components/data/data-table-new/directives/datatable-pagination.directive';
import { DataTableSortState } from '@components/data/data-table-new/directives/datatable-sort.directive';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DecodedDataValueService {
  constructor(
    protected http: HttpClient,
    protected divisionService: DivisionService,
    protected appConfigService: AppConfigService
  ) {}

  getByTableIdWithRelationships(
    pageState: DataTablePageState,
    sortState: DataTableSortState,
    search: string,
    filter: string,
    tableId: string
  ): Observable<Page<DecodedDataValue>> {
    const params: SearchParams = {
      search,
      filter,
      pageNumber: pageState.index,
      pageSize: pageState.size,
      sortDirection: sortState.direction.toString().toUpperCase(),
      sortField: sortState.column
    };

    return this.http.post<Page<DecodedDataValue>>(
      `${this.appConfigService.getApi()}/decodeddata/value/search/relationship/${tableId}`,
      params
    );
  }

  getByTableId(
    pageState: DataTablePageState,
    sortState: DataTableSortState,
    search: string,
    filter: string,
    tableId: string
  ): Observable<Page<DecodedDataValue>> {
    const params: SearchParams = {
      search,
      filter,
      pageNumber: pageState.index,
      pageSize: pageState.size,
      sortDirection: sortState.direction.toString().toUpperCase(),
      sortField: sortState.column
    };

    return this.http.post<Page<DecodedDataValue>>(
      `${this.appConfigService.getApi()}/decodeddata/value/search/${tableId}`,
      params
    );
  }

  getByTableName(
    pageState: DataTablePageState,
    sortState: DataTableSortState,
    search: string,
    filter: string,
    tableName: string
  ): Observable<Page<DecodedDataValue>> {
    const params: SearchParams = {
      search,
      filter,
      pageNumber: pageState.index,
      pageSize: pageState.size,
      sortDirection: sortState.direction.toString().toUpperCase(),
      sortField: sortState.column
    };

    return this.http.post<Page<DecodedDataValue>>(
      `${this.appConfigService.getApi()}/decodeddata/value/name/search/${tableName}`,
      params
    );
  }

  getAllByTableNames(tableNames: string[]): Observable<{ [s: string]: DecodedDataValue[] }> {
    return this.http.post<{ [s: string]: DecodedDataValue[] }>(
      `${this.appConfigService.getApi()}/decodeddata/value/all/`,
      tableNames
    );
  }

  getAllByTableName(tableName: string): Observable<Array<DecodedDataValue>> {
    return this.http.get<Array<DecodedDataValue>>(
      `${this.appConfigService.getApi()}/decodeddata/value/all/${tableName}`
    );
  }

  getAllDtoByTableName(tableName: string): Observable<Array<DecodedDataValueDto>> {
    return this.http.get<Array<DecodedDataValueDto>>(
      `${this.appConfigService.getApi()}/decodeddata/value/all/dto/${tableName}`
    );
  }

  getNew(decodedTable: DecodedDataTable): Observable<DecodedDataValue> {
    return this.divisionService.getAll().pipe(
      map(divisions => {
        return {
          id: null,
          code: null,
          txDescription: new EmptyTranslation(),
          standard: false,
          activatedState: decodedTable.masterState,
          extraField1Value: '',
          extraField2Value: '',
          extraField3Value: '',
          active: true,
          libraryItemId: null,
          valueRelationships: [],
          divisions: divisions.length === 1 ? [divisions[0]] : []
        };
      })
    );
  }

  getByIds(ids: string[]): Observable<DecodedDataValue[]> {
    return this.http.post<DecodedDataValue[]>(`${this.appConfigService.getApi()}/decodeddata/value/ids`, ids);
  }

  /**
    Resolve the decoded values by using their ids without applying the divisional check. 
   */
  resolveValues(ids: string[]): Observable<DecodedDataValue[]> {
    return this.http.post<DecodedDataValue[]>(`${this.appConfigService.getApi()}/decodeddata/value/ids/resolve`, ids);
  }

  save(tableId: string, decodedDataValue: DecodedDataValue): Observable<string> {
    const params = new HttpParams().append('tableId', tableId);

    return decodedDataValue.id
      ? this.http.put<string>(`${this.appConfigService.getApi()}/decodeddata/value`, decodedDataValue, { params })
      : this.http.post<string>(`${this.appConfigService.getApi()}/decodeddata/value`, decodedDataValue, { params });
  }

  getGrandParentValues(
    pageState: DataTablePageState,
    sortState: DataTableSortState,
    search: string,
    filter: string,
    childValueId: string,
    tableId: string
  ): Observable<Page<DecodedDataValue>> {
    return this.http.post<Page<DecodedDataValue>>(
      `${this.appConfigService.getApi()}/decodeddata/integration/value/search/${tableId}`,
      {
        integrationParams: {
          outputType: 'PARENT',
          childValueId
        },
        searchParams: {
          search,
          filter,
          pageNumber: pageState.index,
          pageSize: pageState.size,
          sortDirection: sortState.direction.toString().toUpperCase(),
          sortField: sortState.column
        }
      }
    );
  }

  getValues(
    pageState: DataTablePageState,
    sortState: DataTableSortState,
    search: string,
    filter: string,
    outputType: string,
    tableId: string,
    childValueId?: string,
    parentValueId?: string,
    grandParentValueId?: string
  ): Observable<Page<DecodedDataValue>> {
    return this.http.post<Page<DecodedDataValue>>(
      `${this.appConfigService.getApi()}/decodeddata/integration/value/search/${tableId}`,
      {
        integrationParams: {
          outputType,
          childValueId,
          parentValueId,
          grandParentValueId
        },
        searchParams: {
          search,
          filter,
          pageNumber: pageState.index,
          pageSize: pageState.size,
          sortDirection: sortState.direction.toString().toUpperCase(),
          sortField: sortState.column
        }
      }
    );
  }

  getValuesDto(
    pageState: DataTablePageState,
    sortState: DataTableSortState,
    search: string,
    filter: string,
    outputType: string,
    tableId: string,
    childValueId?: string,
    parentValueId?: string,
    grandParentValueId?: string
  ): Observable<Page<DecodedDataValueDto>> {
    return this.http.post<Page<DecodedDataValueDto>>(
      `${this.appConfigService.getApi()}/decodeddata/integration/value/dto/search/${tableId}`,
      {
        integrationParams: {
          outputType,
          childValueId,
          parentValueId,
          grandParentValueId
        },
        searchParams: {
          search,
          filter,
          pageNumber: pageState.index,
          pageSize: pageState.size,
          sortDirection: sortState.direction.toString().toUpperCase(),
          sortField: sortState.column
        }
      }
    );
  }

  getAllValuesByParentValueId(tableName: string, parentValueId: string): Observable<Array<DecodedDataValue>> {
    return this.http.post<Array<DecodedDataValue>>(
      `${this.appConfigService.getApi()}/decodeddata/integration/value/all/${tableName}`,
      {
        parentValueId,
        outputType: 'CHILD'
      }
    );
  }

  delete(id: string) {
    return this.http.delete(`${this.appConfigService.getApi()}/decodeddata/value/${id}`);
  }

  assignDivisions(valuesIds: Array<string>, divisions: Array<Division>): Observable<void> {
    const params = new HttpParams()
      .append('valuesIds', valuesIds.join(', '))
      .append('divisionsIds', divisions.map(div => div.id).join(', '));
    return this.http.post<void>(`${this.appConfigService.getApi()}/decodeddata/value/assigndivision`, params);
  }

  assignAllDivisions(tableId: string, divisions: Array<Division>): Observable<void> {
    const params = new HttpParams()
      .append('tableId', tableId)
      .append('divisionsIds', divisions.map(div => div.id).join(', '));
    return this.http.post<void>(`${this.appConfigService.getApi()}/decodeddata/value/assigndivision/all`, params);
  }
}
